import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { cn } from '@/lib/utils';
import {
    segmentedControlButtonVariants,
    segmentedControlListVariants,
    segmentedControlTrackVariants,
} from './segmented-control-styles';
import type { IconComponent } from '@/components/icons';

type UnderlineTabItem = {
    value: string;
    label: React.ReactNode;
    content: React.ReactNode;
    icon?: IconComponent;
    disabled?: boolean;
};

type UnderlineTabsProps = Omit<
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    'children' | 'defaultValue'
> & {
    tabs: UnderlineTabItem[];
    defaultValue?: string;
    headerAction?: React.ReactNode;
    headerClassName?: string;
    actionClassName?: string;
    listClassName?: string;
    triggerClassName?: string;
    contentClassName?: string;
};

const UnderlineTabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Root>,
    UnderlineTabsProps
>(
    (
        {
            tabs,
            defaultValue,
            value,
            className,
            headerAction,
            headerClassName,
            actionClassName,
            listClassName,
            triggerClassName,
            contentClassName,
            ...props
        },
        ref,
    ) => {
        if (tabs.length === 0) return null;

        return (
            <TabsPrimitive.Root
                ref={ref}
                value={value}
                defaultValue={value === undefined ? (defaultValue ?? tabs[0].value) : undefined}
                className={cn('space-y-6', className)}
                {...props}
            >
                <div className={cn('flex w-full items-end justify-between gap-3', headerClassName)}>
                    <div
                        className={cn(
                            segmentedControlListVariants({ variant: 'underline' }),
                            listClassName,
                        )}
                    >
                        <TabsPrimitive.List
                            className={segmentedControlTrackVariants({ variant: 'underline' })}
                        >
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <TabsPrimitive.Trigger
                                        key={tab.value}
                                        value={tab.value}
                                        disabled={tab.disabled}
                                        className={cn(
                                            segmentedControlButtonVariants({
                                                variant: 'underline',
                                                active: false,
                                            }),
                                            'data-[state=active]:border-brand data-[state=active]:text-brand',
                                            triggerClassName,
                                        )}
                                    >
                                        {Icon ? <Icon className="size-4 shrink-0" /> : null}
                                        <span>{tab.label}</span>
                                    </TabsPrimitive.Trigger>
                                );
                            })}
                        </TabsPrimitive.List>
                    </div>

                    {headerAction ? (
                        <div className={cn('shrink-0 pb-2', actionClassName)}>{headerAction}</div>
                    ) : null}
                </div>

                {tabs.map((tab) => (
                    <TabsPrimitive.Content
                        key={tab.value}
                        value={tab.value}
                        className={cn(
                            'mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:data-[state=active]:duration-200 motion-safe:data-[state=active]:animate-in motion-safe:data-[state=active]:fade-in-0 motion-safe:data-[state=active]:slide-in-from-bottom-2',
                            contentClassName,
                        )}
                    >
                        {tab.content}
                    </TabsPrimitive.Content>
                ))}
            </TabsPrimitive.Root>
        );
    },
);

UnderlineTabs.displayName = 'UnderlineTabs';

export default UnderlineTabs;
